type NetworkStatus = {
  isOnline: boolean;
  connectionType?: string;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
};

type NetworkEvent = {
  type: "online" | "offline" | "change";
  status: NetworkStatus;
};

type NetworkInformation = EventTarget & {
  type?: string;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  addEventListener(type: string, listener: EventListener): void;
  removeEventListener(type: string, listener: EventListener): void;
};

type NavigatorWithConnection = Navigator & {
  connection?: NetworkInformation;
};

class NetworkMonitor {
  private isOnline = navigator.onLine;
  private listeners: ((event: NetworkEvent) => void)[] = [];
  private connection: NetworkInformation | null = null;
  private pollingInterval: ReturnType<typeof setInterval> | null = null;

  private handleOnline = (): void => {
    this.isOnline = true;
    this.notifyListeners({ type: "online", status: this.getNetworkStatus() });
  };

  private handleOffline = (): void => {
    this.isOnline = false;
    this.notifyListeners({ type: "offline", status: this.getNetworkStatus() });
  };

  private handleConnectionChange = (): void => {
    this.notifyListeners({ type: "change", status: this.getNetworkStatus() });
  };

  constructor() {
    this.initialize();
  }

  private initialize(): void {
    if (typeof window === "undefined") {
      return;
    }
    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);

    if ("connection" in navigator) {
      this.connection =
        (navigator as NavigatorWithConnection).connection || null;

      if (this.connection) {
        this.connection.addEventListener(
          "change",
          this.handleConnectionChange as EventListener
        );
      }
    }

    this.pollingInterval = setInterval(() => {
      const wasOnline = this.isOnline;
      this.isOnline = navigator.onLine;

      if (wasOnline !== this.isOnline) {
        this.notifyListeners({
          type: this.isOnline ? "online" : "offline",
          status: this.getNetworkStatus(),
        });
      }
    }, 5000);
  }

  addListener(listener: (event: NetworkEvent) => void): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  private notifyListeners(event: NetworkEvent): void {
    this.listeners.forEach((listener) => listener(event));
  }

  getNetworkStatus(): NetworkStatus {
    const status: NetworkStatus = {
      isOnline: this.isOnline,
    };

    if (this.connection) {
      status.connectionType = this.connection.type;
      status.effectiveType = this.connection.effectiveType;
      status.downlink = this.connection.downlink;
      status.rtt = this.connection.rtt;
    }

    return status;
  }

  isCurrentlyOnline(): boolean {
    return this.isOnline;
  }

  getConnectionQuality(): "excellent" | "good" | "fair" | "poor" | "unknown" {
    if (!this.connection) {
      return "unknown";
    }

    const { effectiveType, downlink, rtt } = this.connection;

    if (typeof downlink === "undefined" || typeof rtt === "undefined") {
      return "unknown";
    }

    if (effectiveType === "4g" && downlink > 10 && rtt < 100) {
      return "excellent";
    }
    if (effectiveType === "4g" && downlink > 5 && rtt < 200) {
      return "good";
    }
    if (effectiveType === "3g" || (downlink > 1 && rtt < 500)) {
      return "fair";
    }
    return "poor";
  }

  destroy(): void {
    if (typeof window === "undefined") {
      return;
    }
    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("offline", this.handleOffline);

    if (this.connection) {
      this.connection.removeEventListener(
        "change",
        this.handleConnectionChange as EventListener
      );
    }

    if (this.pollingInterval !== null) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }

    this.listeners = [];
  }
}

export const networkMonitor = new NetworkMonitor();
export type { NetworkStatus };
